"use client";

import React from "react";
import { Modal as AntModal, ConfigProvider } from "antd";
import type { ModalProps as AntModalProps } from "antd";
import { orionTheme } from "../theme/orion-theme";
import clsx from "clsx";
import "../styles/modal.css";

export interface ModalProps extends AntModalProps {
  /**
   * Additional CSS classes (supports Tailwind overrides)
   */
  className?: string;

  /**
   * Modal variant
   */
  variant?: "default" | "compact" | "large";

  /**
   * Show header border
   */
  showHeaderBorder?: boolean;

  /**
   * Show footer border
   */
  showFooterBorder?: boolean;
}

/**
 * Modal component - Ant Design Modal with OpenStore Orion theming
 *
 * This component provides FULL access to all Ant Design Modal props while
 * automatically applying the OpenStore Orion theme and Figma design specifications.
 *
 * Features:
 * - Figma design specifications (8px border radius, specific colors, shadows)
 * - Automatic theming
 * - Tailwind class override support
 * - ALL Ant Design Modal features and props
 * - Custom variants (default, compact, large)
 * - Header and footer border options
 *
 * @example
 * ```tsx
 * // Basic modal with Orion theming
 * <Modal
 *   open={isOpen}
 *   onCancel={handleCancel}
 *   title="Add Products"
 *   footer={[
 *     <Button key="close" onClick={handleCancel}>Close</Button>,
 *     <Button key="add" type="primary" onClick={handleAdd}>Add</Button>
 *   ]}
 * >
 *   Modal content here
 * </Modal>
 *
 * // With custom variant and borders
 * <Modal
 *   open={isOpen}
 *   onCancel={handleCancel}
 *   title="Settings"
 *   variant="compact"
 *   showHeaderBorder
 *   showFooterBorder
 *   className="custom-modal"
 * >
 *   Settings content
 * </Modal>
 *
 * // All Ant Design Modal props work
 * <Modal
 *   open={isOpen}
 *   onCancel={handleCancel}
 *   onOk={handleOk}
 *   title="Confirm Action"
 *   okText="Confirm"
 *   cancelText="Cancel"
 *   centered
 *   maskClosable={false}
 *   closable={true}
 *   destroyOnClose
 *   width={600}
 *   zIndex={1000}
 * />
 * ```
 */
export const Modal: React.FC<ModalProps> = ({
  className,
  variant = "default",
  showHeaderBorder = false,
  showFooterBorder = false,
  ...props
}) => {
  const getVariantClass = (variant: string) => {
    switch (variant) {
      case "compact":
        return "orion-modal--compact";
      case "large":
        return "orion-modal--large";
      case "default":
      default:
        return "orion-modal--default";
    }
  };

  return (
    <ConfigProvider theme={orionTheme}>
      <AntModal
        className={clsx(
          "orion-modal",
          getVariantClass(variant),
          {
            "orion-modal--header-border": showHeaderBorder,
            "orion-modal--footer-border": showFooterBorder,
          },
          className
        )}
        {...props}
      />
    </ConfigProvider>
  );
};

Modal.displayName = "Modal";

// Export convenience components
export const ConfirmModal: React.FC<
  Omit<ModalProps, "footer" | "okText" | "cancelText">
> = (props) => <Modal okText="Confirm" cancelText="Cancel" {...props} />;

export const InfoModal: React.FC<
  Omit<ModalProps, "footer" | "okText" | "cancelText">
> = (props) => <Modal okText="OK" cancelText="Cancel" {...props} />;

export const WarningModal: React.FC<
  Omit<ModalProps, "footer" | "okText" | "cancelText">
> = (props) => <Modal okText="OK" cancelText="Cancel" {...props} />;
